import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildBacklinksMd } from "@/lib/mdBuilders";
import { Lock, RefreshCw, Clock } from "lucide-react";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { DisavowPanel } from "@/components/DisavowPanel";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";

const TOTAL_METRIC = {
    "Backlinks": "total_backlinks",
    "Ref. Domains": "referring_domains",
    "DR": "domain_rating",
    "Toxic": "toxic_backlinks",
    "DoFollow": "dofollow_links",
    "NoFollow": "nofollow_links",
};

// Human-readable "3h ago" style label from an ISO timestamp
const relativeAge = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diffMs = Date.now() - then;
    if (diffMs < 0) return "just now";
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

export default function Backlinks() {
    const project = useProject();
    const [data, setData] = useState(null);
    const [locked, setLocked] = useState(false);
    const [tab, setTab] = useState("all");

    useEffect(() => {
        if (!project) return;
        // Iter34: GET is read-only (cache-only) — never spends credits.
        client.get(`/projects/${project.id}/backlinks`).then((r) => setData(r.data)).catch((e) => {
            if (e?.response?.status === 402) setLocked(true);
        });
    }, [project]);

    const refresh = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/backlinks/refresh`);
            setData(r.data);
            toast.success("Backlinks refreshed from DataForSEO");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        }
    };

    if (locked) return (
        <div className="space-y-6" data-testid="backlinks-page">
            <div>
                <div className="overline">Off-page</div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Backlinks</h1>
            </div>
            <div className="border border-primary/60 bg-primary/5 p-10 text-center" data-testid="backlinks-locked">
                <Lock className="w-8 h-8 mx-auto text-primary mb-4" strokeWidth={1.5} />
                <div className="font-display text-2xl">Backlink monitoring is an Agency feature</div>
                <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
                    Full referring-domain analysis, toxic-link detection, and disavow-ready exports are unlocked on the $299/mo Agency plan.
                </p>
                <Link to="/pricing" data-testid="backlinks-upgrade-link" className="mt-6 inline-block bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90">
                    View Agency plan →
                </Link>
            </div>
        </div>
    );

    if (!project || !data) return <div className="text-muted-foreground font-mono text-sm">loading backlinks…</div>;

    if (data.available === false) {
        return (
            <div className="space-y-6" data-testid="backlinks-page">
                <div>
                    <div className="overline">Off-page</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Backlinks</h1>
                    <p className="text-sm text-muted-foreground mt-2">Referring domains, toxicity, and disavow-ready insights.</p>
                </div>
                <div className="border border-border bg-card/40 p-10 text-center" data-testid="backlinks-empty-state">
                    <Clock className="w-8 h-8 mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
                    <div className="font-display text-2xl">
                        {data.reason === "not_configured" ? "DataForSEO isn't connected yet" : "No backlink data pulled yet"}
                    </div>
                    <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
                        {data.reason === "not_configured"
                            ? "Backlink monitoring needs a live DataForSEO connection configured by your admin."
                            : "Pull a real backlinks summary + referring-domains list from DataForSEO to see this project's off-page profile."}
                    </p>
                    {data.reason !== "not_configured" && (
                        <div className="mt-6 flex justify-center">
                            <CreditActionButton
                                actionKey="backlinks_refresh"
                                testid="refresh-backlinks-btn-empty"
                                onConfirm={refresh}
                                description="Pulls a fresh backlinks summary + referring domains list from DataForSEO. Cache is reused within a 24h window at no cost."
                            >
                                <span className="inline-flex items-center gap-1">
                                    <RefreshCw className="w-4 h-4" /> Pull backlinks data
                                </span>
                            </CreditActionButton>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const t = data.totals;
    const rows = tab === "toxic" ? data.sources.filter((s) => s.toxic_score >= 60) : data.sources;

    return (
        <div className="space-y-6" data-testid="backlinks-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Off-page</div>
                    <div className="flex items-center gap-3 mt-2">
                        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Backlinks</h1>
                        <DataSourceBadge source={data.source || "mock"} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">Referring domains, toxicity, and disavow-ready insights.</p>
                </div>
                {(data.cached_at || data.next_refresh_credits) && (
                    <div className="flex flex-col items-end gap-2">
                        <div
                            data-testid="backlinks-freshness-badge"
                            title={`Backlink data is cached for ${data.cache_ttl_hours || 24}h.`}
                            className="border border-border bg-card/40 px-3 py-2 flex items-center gap-3 text-xs font-mono"
                        >
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            <div className="flex flex-col leading-tight">
                                <span data-testid="backlinks-refreshed-label" className="text-foreground">
                                    {data.cached_at ? `Refreshed ${relativeAge(data.cached_at)}` : "Cache empty · mock data"}
                                </span>
                                <span className="text-muted-foreground flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3" />
                                    <span data-testid="backlinks-next-refresh-cost">
                                        refresh charges {data.next_refresh_credits || 2} credits
                                    </span>
                                </span>
                            </div>
                        </div>
                        <CreditActionButton
                            actionKey="backlinks_refresh"
                            testid="refresh-backlinks-btn"
                            onConfirm={refresh}
                            description="Pulls a fresh backlinks summary + referring domains list from DataForSEO. Cache is reused within a 24h window at no cost."
                        >
                            <span className="inline-flex items-center gap-1">
                                <RefreshCw className="w-4 h-4" /> Refresh backlinks
                            </span>
                        </CreditActionButton>
                        <DownloadMdButton
                            filenamePrefix={`inphase-backlinks-${project?.domain || "project"}`}
                            buildMarkdown={() => buildBacklinksMd({ project, data })}
                            testId="download-backlinks-md"
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {[
                    ["Backlinks", t.backlinks.toLocaleString()],
                    ["Ref. Domains", t.referring_domains.toLocaleString()],
                    ["DR", t.domain_rating],
                    ["Toxic", t.toxic_domains, "text-destructive"],
                    ["DoFollow", t.dofollow.toLocaleString()],
                    ["NoFollow", t.nofollow.toLocaleString()],
                ].map(([l, v, c]) => (
                    <div key={l} className="border border-border bg-card/40 p-4">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">{l} <MetricInfo metricKey={TOTAL_METRIC[l]} /></div>
                        <div className={`font-mono text-2xl mt-1 ${c || ""}`}>{v}</div>
                    </div>
                ))}
            </div>

            <DisavowPanel projectId={project.id} />

            <div className="border border-border bg-card/40">
                <div className="p-4 border-b border-border flex items-center gap-2">
                    <button data-testid="tab-all" onClick={() => setTab("all")}
                            className={`px-3 py-1.5 text-sm border ${tab === "all" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
                        All ({data.sources.length})
                    </button>
                    <button data-testid="tab-toxic" onClick={() => setTab("toxic")}
                            className={`px-3 py-1.5 text-sm border ${tab === "toxic" ? "border-destructive text-destructive" : "border-transparent text-muted-foreground"}`}>
                        Toxic ({data.sources.filter((s) => s.toxic_score >= 60).length})
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="text-left p-3">Domain</th>
                                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">DR <MetricInfo metricKey="domain_rating" /></span></th>
                                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Backlinks <MetricInfo metricKey="total_backlinks" /></span></th>
                                <th className="text-right p-3">Traffic</th>
                                <th className="text-left p-3">Type</th>
                                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Toxic <MetricInfo metricKey="toxic_score" /></span></th>
                                <th className="text-right p-3">First seen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s, i) => (
                                <tr key={i} className="border-b border-border/50 hover:bg-accent/20">
                                    <td className="p-3 font-mono">{s.domain}</td>
                                    <td className="p-3 text-right font-mono">{s.dr}</td>
                                    <td className="p-3 text-right font-mono text-muted-foreground">{s.backlinks}</td>
                                    <td className="p-3 text-right font-mono text-muted-foreground">{s.traffic.toLocaleString()}</td>
                                    <td className="p-3 text-xs">{s.type}</td>
                                    <td className={`p-3 text-right font-mono ${s.toxic_score >= 60 ? "text-destructive" : "text-muted-foreground"}`}>{s.toxic_score}</td>
                                    <td className="p-3 text-right text-xs text-muted-foreground">{s.first_seen}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
